import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  inject
} from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule
} from "@angular/forms";
import { COMMA, ENTER } from "@angular/cdk/keycodes";
import { Observable, Subject } from "rxjs";
import { debounceTime, map, startWith, takeUntil } from "rxjs/operators";
import { ResourceModel } from "@app/models/rest/resource";
import { VisitType } from "@app/models/rest/visit-type";
import { ResourceRestService } from "@app/services/hal/resource-rest.service";
import { VisitTypeRestService } from "@app/services/hal/visit-type-rest.service";
import moment from "moment";
import { LocationChooserComponent } from "@app/view/shared/location-chooser/location-chooser.component";
import { MeetingService } from "@app/services/api/v1/meeting/meeting.service";
import {
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
  MatAutocomplete
} from "@angular/material/autocomplete";

import { MatSnackBar } from "@angular/material/snack-bar";
import {
  MatChipInputEvent,
  MatChipGrid,
  MatChipRow,
  MatChipRemove,
  MatChipInput
} from "@angular/material/chips";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
  MatDialogContent,
  MatDialogActions
} from "@angular/material/dialog";
import { ConfirmDialogComponent } from "@app/view/shared/confirm-dialog/confirm-dialog.component";
import { ClientConfigService } from "@app/core/config/client-config.service";
import { CautionService } from "@app/integrations/urm/services/caution.service";
import { PropertyService } from "@app/integrations/urm/services/property.service";
import { PropPropertyHeader } from "@app/integrations/urm/models/prop-property-header";
import { PropertySearchComponent } from "@app/integrations/urm/views/property-search/property-search.component";
import { MeetingBulkRequest } from "@app/services/api/v1/meeting/dto/meeting-bulk-request";
import { CdkScrollable } from "@angular/cdk/scrolling";
import { MatToolbar } from "@angular/material/toolbar";
import {
  MatFormField,
  MatLabel,
  MatSuffix
} from "@angular/material/form-field";
import { MatInput } from "@angular/material/input";
import { MatSelect } from "@angular/material/select";
import { MatOption } from "@angular/material/core";
import {
  MatDatepickerInput,
  MatDatepickerToggle,
  MatDatepicker
} from "@angular/material/datepicker";
import { MatCheckbox } from "@angular/material/checkbox";
import { NgClass, AsyncPipe } from "@angular/common";
import { MatTooltip } from "@angular/material/tooltip";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { MatIcon } from "@angular/material/icon";
import { MatIconButton, MatButton } from "@angular/material/button";
import { PropertyInfoComponent } from "../../../integrations/urm/views/property-info/property-info.component";
import { DialogHeaderComponent } from "@app/view/shared/dialog-header/dialog-header.component";

@Component({
  selector: "app-meeting-editor",
  templateUrl: "./meeting-editor.component.html",
  styleUrls: ["./meeting-editor.component.scss"],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    CdkScrollable,
    MatDialogContent,
    MatFormField,
    MatLabel,
    MatInput,
    MatSelect,
    MatOption,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    MatCheckbox,
    MatChipGrid,
    MatChipRow,
    NgClass,
    MatTooltip,
    MatProgressSpinner,
    MatChipRemove,
    MatIcon,
    MatChipInput,
    MatAutocompleteTrigger,
    MatAutocomplete,
    MatIconButton,
    PropertyInfoComponent,
    MatDialogActions,
    MatButton,
    AsyncPipe,
    DialogHeaderComponent
  ]
})
export class MeetingEditorComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private resourceService = inject(ResourceRestService);
  private visitTypeService = inject(VisitTypeRestService);
  private dialog = inject(MatDialog);
  private dialogRef =
    inject<MatDialogRef<MeetingEditorComponent>>(MatDialogRef);
  private meetingService = inject(MeetingService);
  private snackBar = inject(MatSnackBar);
  private clientConfigService = inject(ClientConfigService);
  private cautionService = inject(CautionService);
  private propertyService = inject(PropertyService);
  data = inject<{
    currentDate: Date;
  }>(MAT_DIALOG_DATA);

  @ViewChild("resourceInput") resourceInput: ElementRef<HTMLInputElement>;

  meetingForm: FormGroup;
  selectedResources: {
    resource: ResourceModel;
    status: string;
    conflicts: string[];
    loading: boolean;
  }[] = [];
  separatorKeysCodes: number[] = [ENTER, COMMA];
  filteredResources: Observable<ResourceModel[]>;
  allResources: ResourceModel[] = [];
  visitTypes: VisitType[] = [];
  minDate: Date = new Date();

  locationDisplay: string = "";
  showWeekdays: boolean = false;
  showEndDate: boolean = false;
  showEveryXDays: boolean = false;
  meetingSummary: string = "";
  private unsubscribe$ = new Subject<void>();

  public urmEnabled: boolean;

  constructor() {
    this.urmEnabled = this.clientConfigService?.config?.urmServices?.enabled;
  }

  ngOnInit(): void {
    const originDate = this.getPastMonday();

    this.meetingForm = this.fb.group({
      description: ["", Validators.required],
      visitTypeId: ["", Validators.required],
      startDate: [this.data.currentDate, Validators.required],
      startTime: ["09:00", Validators.required],
      duration: [30, [Validators.required, Validators.min(1)]],
      recurrenceType: ["Once", Validators.required],
      monday: [true],
      tuesday: [true],
      wednesday: [true],
      thursday: [true],
      friday: [true],
      saturday: [true],
      sunday: [true],
      everyXDays: [1, [Validators.required, Validators.min(1)]],
      endDate: [this.data.currentDate, Validators.required],
      originDate: [this.data.currentDate],
      resourceCtrl: [""],
      commit: [false],
      defaultAction: [true],
      postcode: [""],
      latitude: [null],
      longitude: [null],
      // Field to store external property reference if URM is enabled
      externalPropertyReference: [null]
    });

    this.adjustFormControls(this.meetingForm.get("recurrenceType").value);

    this.meetingForm
      .get("recurrenceType")
      .valueChanges.pipe(takeUntil(this.unsubscribe$))
      .subscribe((value) => {
        this.adjustFormControls(value);
        this.calculateMeetingInstances();
        this.recheckAllResources();
      });

    this.meetingForm
      .get("startDate")
      .valueChanges.pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        const recurrenceType = this.meetingForm.get("recurrenceType").value;
        if (recurrenceType === "Once" || recurrenceType === "Custom") {
          this.meetingForm
            .get("originDate")
            .setValue(this.meetingForm.get("startDate").value);
        }
        this.calculateMeetingInstances();
        this.recheckAllResources();
      });

    this.meetingForm
      .get("startTime")
      .valueChanges.pipe(debounceTime(300), takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.calculateMeetingInstances();
        this.recheckAllResources();
      });

    this.meetingForm
      .get("duration")
      .valueChanges.pipe(debounceTime(300), takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.calculateMeetingInstances();
        this.recheckAllResources();
      });

    this.meetingForm
      .get("endDate")
      .valueChanges.pipe(debounceTime(300), takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.calculateMeetingInstances();
        this.recheckAllResources();
      });

    this.meetingForm
      .get("everyXDays")
      .valueChanges.pipe(debounceTime(300), takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.calculateMeetingInstances();
        this.recheckAllResources();
      });

    [
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
      "sunday"
    ].forEach((day) => {
      this.meetingForm
        .get(day)
        .valueChanges.pipe(debounceTime(300), takeUntil(this.unsubscribe$))
        .subscribe(() => {
          this.calculateMeetingInstances();
          this.recheckAllResources();
        });
    });

    this.loadResources();
    this.loadVisitTypes();

    this.filteredResources = this.meetingForm
      .get("resourceCtrl")
      .valueChanges.pipe(
        startWith(""),
        map((value: string | null) =>
          value ? this._filterResources(value) : this._filterResources("")
        )
      );
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  adjustFormControls(value: string) {
    if (value === "Once") {
      this.showWeekdays = false;
      this.showEndDate = false;
      this.showEveryXDays = false;
      this.meetingForm
        .get("originDate")
        .setValue(this.meetingForm.get("startDate").value);
    } else if (value === "Weekly") {
      this.showWeekdays = true;
      this.showEndDate = true;
      this.showEveryXDays = false;
      const originDate = this.getPastMonday();
      this.meetingForm.get("originDate").setValue(originDate);
    } else if (value === "Custom") {
      this.showWeekdays = false;
      this.showEndDate = true;
      this.showEveryXDays = true;
      this.meetingForm
        .get("originDate")
        .setValue(this.meetingForm.get("startDate").value);
    }
  }

  calculateMeetingInstances() {
    const formValue = this.meetingForm.value;
    const recurrenceType = formValue.recurrenceType;
    const startDate = moment(formValue.startDate);
    const duration = formValue.duration;
    let totalMeetings = 0;

    if (recurrenceType === "Once") {
      totalMeetings = 1;
    } else if (recurrenceType === "Weekly") {
      const endDate = moment(formValue.endDate);
      const weekdays = [];
      if (formValue.monday) weekdays.push(1);
      if (formValue.tuesday) weekdays.push(2);
      if (formValue.wednesday) weekdays.push(3);
      if (formValue.thursday) weekdays.push(4);
      if (formValue.friday) weekdays.push(5);
      if (formValue.saturday) weekdays.push(6);
      if (formValue.sunday) weekdays.push(0);

      let current = moment(startDate);
      totalMeetings = 0;
      while (current.isSameOrBefore(endDate, "day")) {
        if (weekdays.includes(current.day())) {
          totalMeetings++;
        }
        current.add(1, "day");
      }
    } else if (recurrenceType === "Custom") {
      const endDate = moment(formValue.endDate);
      const everyXDays = formValue.everyXDays;
      const daysDiff = endDate.diff(startDate, "days");
      totalMeetings = Math.floor(daysDiff / everyXDays) + 1;
    }

    this.meetingSummary = `Total meetings to be created: ${totalMeetings}`;
  }

  getPastMonday(): Date {
    const today = moment();
    const lastMonday = today.clone().startOf("week").add(1, "day");
    return lastMonday.toDate();
  }

  loadResources() {
    this.resourceService.getPage().subscribe((resources) => {
      this.allResources = resources.resources;
      this.filteredResources = this.meetingForm
        .get("resourceCtrl")
        .valueChanges.pipe(
          startWith(""),
          map((value: string | null) =>
            value ? this._filterResources(value) : this._filterResources("")
          )
        );
    });
  }

  loadVisitTypes() {
    this.visitTypeService.getPage().subscribe((visitTypes) => {
      this.visitTypes = visitTypes.resources.filter((x) => x.meeting);
      if (this.visitTypes.length === 1) {
        this.meetingForm.get("visitTypeId").setValue(this.visitTypes[0].id);
      }
    });
  }

  addResource(event: MatChipInputEvent): void {
    const input = event.chipInput.inputElement;
    const value = event.value;

    if ((value || "").trim()) {
      const resource = this.allResources.find(
        (r) => r.name.toLowerCase() === value.trim().toLowerCase()
      );
      if (
        resource &&
        !this.selectedResources.some((sr) => sr.resource.id === resource.id)
      ) {
        const resourceEntry = {
          resource: resource,
          status: "Checking",
          conflicts: [],
          loading: true
        };
        this.selectedResources.push(resourceEntry);
        this.checkResourceAvailability(resourceEntry);
      }
    }

    if (input) {
      input.value = "";
    }

    this.meetingForm.get("resourceCtrl").setValue(null);
  }

  removeResource(resourceEntry): void {
    const index = this.selectedResources.indexOf(resourceEntry);
    if (index >= 0) {
      this.selectedResources.splice(index, 1);
    }
    this.meetingForm
      .get("resourceCtrl")
      .setValue(this.meetingForm.get("resourceCtrl").value);
  }

  private _filterResources(
    value: string | ResourceModel | null
  ): ResourceModel[] {
    let filterValue = "";
    if (typeof value === "string") {
      filterValue = value.toLowerCase();
    } else if (value && value.name) {
      filterValue = value.name.toLowerCase();
    }
    const excludedResourceIds = this.selectedResources.map(
      (sr) => sr.resource.id
    );
    return this.allResources.filter(
      (resource) =>
        resource.name.toLowerCase().includes(filterValue) &&
        !excludedResourceIds.includes(resource.id)
    );
  }

  selectedResource(event: MatAutocompleteSelectedEvent): void {
    const resource = event.option.value as ResourceModel;
    if (
      resource &&
      !this.selectedResources.some((sr) => sr.resource.id === resource.id)
    ) {
      const resourceEntry = {
        resource: resource,
        status: "Checking",
        conflicts: [],
        loading: true
      };
      this.selectedResources.push(resourceEntry);
      this.checkResourceAvailability(resourceEntry);
    }
    this.resourceInput.nativeElement.value = "";
    this.meetingForm.get("resourceCtrl").setValue(null);
  }

  // Updated openLocationChooser method
  openLocationChooser(): void {
    if (this.urmEnabled) {
      // Open the property search dialog
      const dialogRef = this.dialog.open(PropertySearchComponent, {
        width: "900px",
        data: {}
      });

      const compInstance = dialogRef.componentInstance;
      compInstance.propertySelected.subscribe(
        (property: PropPropertyHeader) => {
          // When a property is selected, update the form
          this.meetingForm.patchValue({
            postcode: property.Proh_PostCode || "",
            externalPropertyReference: property.Proh_PropertyId
          });
          this.locationDisplay =
            property.Proh_PropertyName || property.Proh_PostCode;
          dialogRef.close();
        }
      );
    } else {
      // Existing non-core location chooser
      const dialogRef = this.dialog.open(LocationChooserComponent, {
        width: "400px",
        data: {}
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result && result.result) {
          const location = result.result;
          this.meetingForm.patchValue({
            postcode: location.postcode || "",
            latitude: location.latitude,
            longitude: location.longitude
          });
          this.locationDisplay =
            location.postcode ||
            `Lat: ${location.latitude}, Lon: ${location.longitude}`;
        }
      });
    }
  }

  clearLocation(): void {
    this.meetingForm.patchValue({
      postcode: "",
      latitude: null,
      longitude: null,
      externalPropertyReference: null
    });
    this.locationDisplay = "";
  }

  onSubmit() {
    if (this.meetingForm.valid) {
      const conflictingResources = this.selectedResources.filter(
        (resourceEntry) => resourceEntry.status === "Conflicts"
      );

      if (conflictingResources.length > 0) {
        this.showConflictWarningDialog(conflictingResources);
      } else {
        this.submitMeeting();
      }
    }
  }

  showConflictWarningDialog(conflictingResources) {
    const conflictMessages = conflictingResources.map(
      (conflict) =>
        `Resource ${conflict.resource.name}: ${conflict.conflicts.join(", ")}`
    );

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: "400px",
      data: {
        message:
          "The following resources are not available for the selected times, and will not be listed as attendees for the following dates:",
        messages: conflictMessages,
        showCancelButton: true
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.submitMeeting();
      }
    });
  }

  submitMeeting() {
    const formValue = this.meetingForm.value;
    const patternEntries = this.generatePatternEntries();
    const request: MeetingBulkRequest = {
      resources: this.selectedResources.map((r) => r.resource.id),
      meetingTypeId: formValue.visitTypeId,
      externalPropertyReference: formValue.externalPropertyReference,
      description: formValue.description,
      pattern: patternEntries,
      originDate: moment(formValue.originDate).toISOString(),
      startDate: moment(formValue.startDate).toISOString(),
      commit: true,
      defaultAction: formValue.defaultAction,
      length: null,
      endDate: null
    };

    const recurrenceType = formValue.recurrenceType;
    if (recurrenceType === "Once") {
      request.length = 1;
      request.endDate = moment(formValue.startDate).toISOString();
    } else if (recurrenceType === "Weekly") {
      request.length = 7;
      request.endDate = moment(formValue.endDate).toISOString();
    } else if (recurrenceType === "Custom") {
      request.length = formValue.everyXDays;
      request.endDate = moment(formValue.endDate).toISOString();
    }

    // Include location data
    if (formValue.postcode) {
      request.postcode = formValue.postcode;
    }
    if (formValue.latitude && formValue.longitude) {
      request.latitude = formValue.latitude;
      request.longitude = formValue.longitude;
    }
    if (this.urmEnabled && formValue.externalPropertyReference) {
      request.externalPropertyReference = formValue.externalPropertyReference;
    }

    this.meetingService.bulk(request).then(
      (response) => {
        this.snackBar.open("Meeting created successfully!", "Close", {
          duration: 3000
        });
        this.dialogRef.close();
      },
      (error) => {
        this.snackBar.open(
          "An error occurred while creating the meeting.",
          "Close",
          {
            duration: 3000
          }
        );
      }
    );
  }

  displayBulkConflicts(conflicts) {
    const conflictMessages = conflicts.map(
      (conflict) =>
        `Resource ID ${conflict.resourceId}: ${conflict.conflicts.join(", ")}`
    );
    this.snackBar.open(
      `Conflicts detected:\n${conflictMessages.join("\n")}`,
      "Close",
      {
        duration: 5000
      }
    );
  }

  generatePatternEntries() {
    const patternEntries = [];
    const formValue = this.meetingForm.value;
    const recurrenceType = formValue.recurrenceType;
    const startTime = moment(formValue.startTime, "HH:mm");
    const duration = formValue.duration;
    const startHour = startTime.hour();
    const startMinute = startTime.minute();

    if (recurrenceType === "Once") {
      patternEntries.push({
        offset: 0,
        startHour: startHour,
        startMinute: startMinute,
        duration: duration
      });
    } else if (recurrenceType === "Weekly") {
      if (formValue.monday) {
        patternEntries.push({ offset: 0, startHour, startMinute, duration });
      }
      if (formValue.tuesday) {
        patternEntries.push({ offset: 1, startHour, startMinute, duration });
      }
      if (formValue.wednesday) {
        patternEntries.push({ offset: 2, startHour, startMinute, duration });
      }
      if (formValue.thursday) {
        patternEntries.push({ offset: 3, startHour, startMinute, duration });
      }
      if (formValue.friday) {
        patternEntries.push({ offset: 4, startHour, startMinute, duration });
      }
      if (formValue.saturday) {
        patternEntries.push({ offset: 5, startHour, startMinute, duration });
      }
      if (formValue.sunday) {
        patternEntries.push({ offset: 6, startHour, startMinute, duration });
      }
    } else if (recurrenceType === "Custom") {
      patternEntries.push({
        offset: 0,
        startHour: startHour,
        startMinute: startMinute,
        duration: duration
      });
    }

    return patternEntries;
  }

  checkResourceAvailability(resourceEntry) {
    const formValue = this.meetingForm.value;
    const recurrenceType = formValue.recurrenceType;
    const patternEntries = this.generatePatternEntries();
    const request = {
      resourceId: resourceEntry.resource.id,

      pattern: patternEntries,
      originDate: moment(formValue.originDate).toISOString(),
      startDate: moment(formValue.startDate).toISOString(),
      endDate:
        recurrenceType === "Once"
          ? moment(formValue.startDate).toISOString()
          : moment(formValue.endDate).toISOString(),
      length:
        recurrenceType === "Custom"
          ? formValue.everyXDays
          : recurrenceType === "Weekly"
            ? 7
            : 1
    };

    resourceEntry.loading = true;
    resourceEntry.status = "Checking";
    resourceEntry.conflicts = [];

    this.meetingService.checkAvailability(request).then(
      (response) => {
        const conflict = response.resourceConflict;
        if (conflict.available) {
          resourceEntry.status = "Available";
          resourceEntry.conflicts = [];
        } else {
          resourceEntry.status = "Conflicts";
          resourceEntry.conflicts = conflict.conflicts;
        }
        resourceEntry.loading = false;
      },
      (error) => {
        resourceEntry.status = "Error";
        resourceEntry.loading = false;
      }
    );
  }

  recheckAllResources() {
    this.selectedResources.forEach((resourceEntry) => {
      this.checkResourceAvailability(resourceEntry);
    });
  }

  onCancel() {
    this.dialogRef.close();
  }
}
